import { observer } from "mobx-react";
import { DefaultLayout } from "../../containers";
import { Button, LinkButton } from "../../components-v2/Button";
import Form from "../../components-v2/Form";
import { PhoneInput } from "../../components-v2/PhoneInput";
import { formatPhoneNumber } from "../../utils/phoneNumbers";
import {
  EditAltinnConnectionForm,
  EmailForm,
  PasswordForm,
  PhoneNumberForm,
  UpdateAltinnSystemCredentialsResult,
  UserSettingsStore,
} from "./userSettingsDomain";
import { FC } from "react";
import { isOk, isPending, PhoneNumber, Result } from "../../common/common";
import { Alert } from "../../components-v2/Alert";
import { MTextInput, TextInput } from "../../components-v2/TextInput";
import { Box, Card, VBox } from "../../components-v2/Box";
import { Icon } from "../../components-v2/Icon";
import { isMobileView } from "../../utils/screen";
import { altinnConnectionDocsUrl } from "../../utils/navigation";
import { Language } from "../../i18n/languages";
import { Tag } from "../../components-v2/Tag";
import { useLanguage, useRootStore, useTranslation } from "../../stores/RootStoreContext";
import { Table } from "../../components-v2/Table";
import { Dialog } from "../../components-v2/Dialog";
import { formatDate } from "../../i18n/dateFormat";
import { CopyToClipboard } from "../../common/commonComponents";

const EditEmail: FC<{ form: EmailForm; save: () => void; cancel: () => void }> = observer(
  ({ form, save, cancel }) => {
    const { t } = useTranslation();
    return (
      <Form submitOnEnter onSubmit={save}>
        <VBox>
          <MTextInput model={form.email} label={t("email")} autofocus disabled={form.isSaving} />
          <Box vertical={isMobileView()}>
            <Button variant={"primary"} label={t("save")} type={"submit"} loading={form.isSaving} />
            <Button
              label={t("cancel")}
              variant={"secondary"}
              onClick={cancel}
              disabled={form.isSaving}
            />
          </Box>
        </VBox>
      </Form>
    );
  },
);

const DisplayEmail: FC<{
  email: string;
  edit: () => void;
  isEmailVerified: boolean;
  startEmailVerification: () => void;
}> = observer(({ email, edit, isEmailVerified, startEmailVerification }) => {
  const { t } = useTranslation();

  return (
    <VBox>
      <div>
        <div className={"label"}>{t("email")}</div>
        <Box>
          <button onClick={edit} data-tooltip={t("user-settings.edit-email")}>
            {email}
            {!email && <span className={"subtle"}>{t("user-settings.missing-email")}</span>}{" "}
            <Icon name={"edit"} color={"info"} />
          </button>
          {email ? (
            isEmailVerified ? (
              <Tag label={t("user-settings.email-verified")} color={"success"} />
            ) : (
              <Tag label={t("user-settings.email-not-verified")} color={"error"} />
            )
          ) : undefined}
        </Box>
      </div>
      <Box>
        {!isEmailVerified && email && (
          <Button
            variant={"primary"}
            label={t("user-settings.email-verify")}
            onClick={startEmailVerification}
          />
        )}
      </Box>
    </VBox>
  );
});

const EmailContainer = observer(({ store }: { store: UserSettingsStore }) => {
  return store.emailForm ? (
    <EditEmail form={store.emailForm} save={store.saveEmail} cancel={store.cancelEditEmail} />
  ) : (
    <DisplayEmail
      email={store.email}
      edit={store.editEmail}
      isEmailVerified={store.isEmailVerified}
      startEmailVerification={store.startEmailVerification}
    />
  );
});

const DisplayPhoneNumber: FC<{ phoneNumber?: PhoneNumber; edit: () => void }> = observer(
  ({ phoneNumber, edit }) => {
    const { t } = useTranslation();
    return (
      <div>
        <div className={"label"}>{t("phone-number-field.number-label")}</div>
        <button data-tooltip={t("user-settings.edit-phone-number")} onClick={edit}>
          <Box>
            {phoneNumber ? (
              formatPhoneNumber(phoneNumber)
            ) : (
              <span className={"subtle"}>{t("user-settings.missing-phone-number")}</span>
            )}
            <Icon name={"edit"} color={"info"} />
          </Box>
        </button>
      </div>
    );
  },
);

const EditPhoneNumber: FC<{ form: PhoneNumberForm; save: () => void; cancel: () => void }> =
  observer(({ form, save, cancel }) => {
    const { t } = useTranslation();
    return (
      <Form submitOnEnter onSubmit={save}>
        <VBox>
          <PhoneInput model={form.phoneNumber} autoFocus={true} disabled={form.isSaving} />
          <Box vertical={isMobileView()}>
            <Button variant={"primary"} label={t("save")} type={"submit"} loading={form.isSaving} />
            <Button
              label={t("cancel")}
              variant={"secondary"}
              onClick={cancel}
              disabled={form.isSaving}
            />
          </Box>
        </VBox>
      </Form>
    );
  });

const PhoneNumberContainer = observer(({ store }: { store: UserSettingsStore }) =>
  store.phoneNumberForm ? (
    <EditPhoneNumber
      form={store.phoneNumberForm}
      save={store.savePhoneNumber}
      cancel={store.cancelEditPhoneNumber}
    />
  ) : (
    <DisplayPhoneNumber phoneNumber={store.phoneNumber} edit={store.editPhoneNumber} />
  ),
);

const UsernameContainer = observer(({ store }: { store: UserSettingsStore }) => {
  const { t } = useTranslation();
  return (
    <div>
      <div className={"label"}>{t("user-settings.username")}</div>
      <div>{store.userName}</div>
    </div>
  );
});

const EditPassword: FC<{ form: PasswordForm; save: () => void; cancel: () => void }> = observer(
  ({ form, save, cancel }) => {
    const { t } = useTranslation();

    return (
      <VBox>
        <div className={"label"}>{t("user-settings.password")}</div>
        <MTextInput
          model={form.oldPassword}
          label={t("user-settings.old-password")}
          type={"password"}
          autofocus
        />
        <MTextInput
          model={form.newPassword}
          label={t("user-settings.new-password")}
          type={"password"}
        />
        <MTextInput
          model={form.confirmedPassword}
          label={t("user-settings.confirm-password")}
          type={"password"}
        />
        <Box vertical={isMobileView()}>
          <Button variant={"primary"} type={"submit"} label={t("save")} onClick={save} />
          <Button label={t("cancel")} variant={"secondary"} onClick={cancel} />
        </Box>
      </VBox>
    );
  },
);

const PasswordPlaceholder: FC<{
  hasPassword: boolean;
  hasEmail: boolean;
  createPasswordResult?: Result;
  createPassword: (locale: Language) => void;
  changePassword: () => void;
}> = observer(({ hasPassword, hasEmail, createPasswordResult, createPassword, changePassword }) => {
  const { t } = useTranslation();
  const locale = useLanguage();

  return (
    <div>
      <div className={"label"}>{t("user-settings.password")}</div>
      {hasPassword ? (
        <button data-tooltip={t("user-settings.change-password")} onClick={changePassword}>
          <div>
            **************** <Icon name={"edit"} color={"info"} />
          </div>
        </button>
      ) : (
        <>
          {!hasEmail ? (
            <Alert
              type={"info"}
              message={t("user-settings.register-email-before-creating-password")}
            />
          ) : isOk(createPasswordResult) ? (
            <Alert type={"info"} message={t("user-settings.create-password-requested")} />
          ) : (
            <Button
              label={t("user-settings.create-password")}
              variant={"secondary"}
              loading={isPending(createPasswordResult)}
              size={"sm"}
              onClick={() => createPassword(locale)}
            />
          )}
        </>
      )}
    </div>
  );
});

const PasswordContainer = observer(({ store }: { store: UserSettingsStore }) => {
  return store.passwordForm ? (
    <EditPassword
      form={store.passwordForm}
      save={store.savePassword}
      cancel={store.cancelChangePassword}
    />
  ) : (
    <PasswordPlaceholder
      hasPassword={store.hasPassword}
      hasEmail={store.hasEmail}
      createPasswordResult={store.createPasswordResult}
      createPassword={store.createPassword}
      changePassword={store.changePassword}
    />
  );
});

export const AltinnVerificationStatus = observer(({ store }: { store: UserSettingsStore }) => {
  const { t } = useTranslation();
  switch (store.updateAltinnSystemCredentialsResult) {
    case UpdateAltinnSystemCredentialsResult.Ok:
      return <Alert type={"success"} message={t("user-settings.altinn.verified")} />;
    case UpdateAltinnSystemCredentialsResult.InvalidSystemUserPassword:
    case UpdateAltinnSystemCredentialsResult.InvalidCredentials:
      return <Alert type={"error"} message={t("user-settings.altinn.unverified")} />;
    case UpdateAltinnSystemCredentialsResult.ConnectionFailure:
      return <Alert type={"warning"} message={t("user-settings.altinn.connection-error")} />;
    default:
      return null;
  }
});

export const EditAltinnSystemCredentials: FC<{
  form: EditAltinnConnectionForm;
  save: () => void;
}> = observer(({ form, save }) => {
  const { t } = useTranslation();
  return (
    <Form submitOnEnter onSubmit={save}>
      <VBox gap={"lg"}>
        <VBox>
          <TextInput
            value={form.altinnSystemId.inputValue}
            onChange={(e) => form.altinnSystemId.setInputValue(e.target.value)}
            label={t("user-settings.altinn.systemID")}
          />
          <TextInput
            value={form.altinnSystemPassword.inputValue}
            onChange={(e) => form.altinnSystemPassword.setInputValue(e.target.value)}
            type={"password"}
            label={t("user-settings.altinn.systemPassword")}
            autoComplete={"new-password"}
          />
        </VBox>
        <Button
          variant={"primary"}
          label={t("user-settings.altinn.connect")}
          type="submit"
          loading={form.isSaving}
        />
      </VBox>
    </Form>
  );
});

export const AltinnSystemCredentialsContainer = observer(
  ({ store }: { store: UserSettingsStore }) => {
    const { t } = useTranslation();
    return (
      <VBox>
        <div>
          <div className={"label"}>{t("user-settings.altinn.systemID")}</div>
          <div>{store.altinnSystemId ?? t("user-settings.altinn.noSystemID")}</div>
        </div>

        <div>
          <div className={"label"}>{t("user-settings.altinn.systemPassword")}</div>
          <div>********</div>
        </div>

        <Box justifyContent={"space-between"} paddingRight={"md"}>
          <Button
            label={t("user-settings.altinn.edit-system-credentials")}
            variant={"secondary"}
            onClick={store.activateEditAltinnSystemCredentials}
          />
          <LinkButton
            icon={"arrow-counter-clockwise"}
            label={t("user-settings.altinn.reset")}
            onClick={store.resetAltinnSystemCredentials}
          />
        </Box>
      </VBox>
    );
  },
);

export const UserSettingsContainer = observer(({ store }: { store: UserSettingsStore }) => {
  const { t } = useTranslation();
  const history = useRootStore().history;
  const locale = useLanguage();
  return (
    <DefaultLayout gap={"lg"} maxWidth={"md"}>
      <Box alignItems={"start"}>
        <LinkButton label={t("back")} onClick={history.goBack} icon={"arrow-left"} />
      </Box>
      <h2>{t("user-settings")}</h2>
      <Card title={t("user-settings.contact-info")} gap={"md"}>
        <EmailContainer store={store} />
        <PhoneNumberContainer store={store} />
      </Card>
      {store.userStore.isNorwegianAuthenticatedCountry && (
        <Card title={t("user-settings.password-and-auth")}>
          <UsernameContainer store={store} />
          <PasswordContainer store={store} />
        </Card>
      )}
      {store.showAltinnSettings && (
        <Card title={t("user-settings.altinn.header")}>
          <AltinnVerificationStatus store={store} />
          <div>
            <a href={altinnConnectionDocsUrl(locale)} target={"_blank"} rel={"noreferrer"}>
              {t("user-settings.altinn.help")}
            </a>{" "}
            <Icon name={"arrow-square-out"} />
          </div>
          {store.editAltinnSystemCredentialsForm ? (
            <EditAltinnSystemCredentials
              form={store.editAltinnSystemCredentialsForm}
              save={store.saveAltinnSystemCredentials}
            />
          ) : store.userSettings?.altinnSystemId ? (
            <AltinnSystemCredentialsContainer store={store} />
          ) : (
            <div>
              <Button
                label={t("user-settings.altinn.add-altinn-connection")}
                variant={"secondary"}
                onClick={store.activateEditAltinnSystemCredentials}
              />
            </div>
          )}
        </Card>
      )}

      <Card title={t("user-settings.api-key.title")}>
        {store.userSettings?.apiKey ? (
          <Table>
            <thead>
              <tr>
                <th />
                <th>{t("user-settings.api-key.active")}</th>
                <th>{t("user-settings.api-key.expires-at")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{store.userSettings?.apiKey.key.substring(0, 4)}**************</td>
                <td>{store.userSettings.apiKey.isActive ? t("yes") : t("no")}</td>
                <td>{formatDate(locale, store.userSettings.apiKey.expiresAt)}</td>
                <td>
                  {store.userSettings.apiKey && (
                    <CopyToClipboard text={store.userSettings.apiKey.key} />
                  )}
                </td>
              </tr>
            </tbody>
          </Table>
        ) : (
          <Box>
            <Button
              variant={"secondary"}
              label={t("create")}
              onClick={() => store.createApiKey()}
            />
          </Box>
        )}
      </Card>

      {store.apiKey && (
        <Dialog
          title={t("user-settings.api-key.title")}
          cancelLabel={t("close")}
          onCancel={store.closeApiKeyDialog}
        >
          <Box alignItems={"center"}>
            {t("user-settings.api-key.description")} <Box fg={"info"}>{store.apiKey.key}</Box>
            <CopyToClipboard text={store.apiKey?.key} />
          </Box>
        </Dialog>
      )}
    </DefaultLayout>
  );
});
